import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PandaLoader from '../PandaLoader';
import { isSpecialEpisode, getSpecialLabel } from '../../config/originalsSpecials';
import { useStoryboard, useProgressHover, ScrubPreview } from './ProgressExtras';

// Shared pieces of Pixley's story players (story page + full-screen/Originals).
// Frosted white glass so the controls sit *in* the picture instead of on top
// of it (the video shows through), Pixley blue for what you press, navy ink,
// Fredoka for the words.

export const PX = {
  ink: '#0B1A33', // text + icons on the glass
  inkSoft: 'rgba(11,26,51,0.62)',
  blue: '#0065F8',
  blueBright: '#0065F8', // progress fill
  accentText: '#0065F8', // blue for words on the glass
  blueDeep: '#073B8E',
  sky: 'rgba(11,26,51,0.12)', // progress track
  paper: 'rgba(255,255,255,0.64)', // the glass
  pale: 'rgba(0,101,248,0.10)', // pills / selected rows
  line: 'rgba(255,255,255,0.7)',
  font: "'Fredoka', 'Inter', sans-serif",
};

export const panelStyle = {
  background: PX.paper,
  border: `1px solid ${PX.line}`,
  boxShadow: '0 8px 28px rgba(11,26,51,0.16)',
  color: PX.ink,
  fontFamily: PX.font,
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
};

export const formatTime = (t) => {
  if (!Number.isFinite(t) || t < 0) return '0:00';
  return `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;
};

/** Index + title of the scene playing at `time`, or null. */
export const sceneAt = (scenes, time) => {
  let found = null;
  (scenes || []).forEach((s, i) => {
    if (s.start <= time) found = { index: i, title: s.title };
  });
  return found;
};

const css = `
.pxp-btn { transition: transform 120ms ease, background 150ms ease, color 150ms ease; }
.pxp-btn:hover { background: rgba(11,26,51,0.07); }
.pxp-btn:active { transform: scale(0.94); }
.pxp-btn:focus-visible, .pxp-play:focus-visible, .pxp-bar:focus-visible { outline: 3px solid #0065F8; outline-offset: 2px; }
.pxp-play { transition: transform 280ms cubic-bezier(0.34,1.56,0.64,1), filter 150ms ease; }
.pxp-play:hover { transform: scale(1.07); filter: brightness(1.08); }
.pxp-play:active { transform: scale(0.88); transition-duration: 90ms; }
@keyframes pxpIcon { from { opacity: 0; transform: scale(0.4) rotate(-70deg); } to { opacity: 1; transform: none; } }
.pxp-icon { animation: pxpIcon 300ms cubic-bezier(0.34,1.56,0.64,1) both; }
.pxp-paint { background: linear-gradient(90deg, #0065F8 0%, #2F8BFF 100%); box-shadow: inset 0 1.5px 0 rgba(255,255,255,0.35); }
.pxp-pip { transform-origin: 50% 100%; transition: opacity 120ms ease; filter: drop-shadow(0 1.5px 1.5px rgba(11,26,51,0.35)); user-select: none; }
@keyframes pxpWaddle { 0% { transform: rotate(-5deg); } 25% { transform: translateY(-3px); } 50% { transform: rotate(5deg); } 75% { transform: translateY(-3px); } 100% { transform: rotate(-5deg); } }
.pxp-pip-walk { animation: pxpWaddle 700ms ease-in-out infinite; }
@keyframes pxpPlop { 0% { transform: translateY(-7px) scale(0.96, 1.05); } 60% { transform: translateY(0) scale(1.08, 0.9); } 100% { transform: none; } }
.pxp-pip-sit { animation: pxpPlop 320ms ease-out both; }
@keyframes pxpWhee { 0%, 100% { transform: rotate(-9deg) translateY(-3px); } 50% { transform: rotate(9deg) translateY(-6px); } }
.pxp-pip-whee { animation: pxpWhee 360ms ease-in-out infinite; }
@keyframes pxpCheer { 0%, 100% { transform: none; } 30% { transform: translateY(-9px) scale(0.95, 1.06); } 55% { transform: scale(1.06, 0.93); } }
.pxp-pip-cheer { animation: pxpCheer 650ms ease-in-out 3 both; }
@keyframes pxpStarPop { 0% { transform: scale(0.4) rotate(-45deg); } 55% { transform: scale(1.5) rotate(10deg); } 100% { transform: none; } }
.pxp-star-lit { animation: pxpStarPop 480ms cubic-bezier(0.34,1.56,0.64,1) both; filter: drop-shadow(0 0 4px rgba(255,201,60,0.8)); }
@keyframes pxpFlag { 0%, 100% { transform: skewY(-5deg); } 50% { transform: skewY(4deg) scaleX(0.94); } }
.pxp-flag { transform-origin: 6px 7px; animation: pxpFlag 1.6s ease-in-out infinite; }
.pxp-nap { transform-origin: 50% 84%; animation: pxpBreathe 3s ease-in-out infinite; filter: drop-shadow(0 2px 2px rgba(11,26,51,0.25)); }
@keyframes pxpBreathe { 0%, 100% { transform: scale(1, 1); } 50% { transform: scale(1.012, 1.045); } }
.pxp-nap-sleep, .pxp-nap-awake { transition: opacity 140ms ease; user-select: none; }
.pxp-nap-awake { opacity: 0; }
.pxp-zzz { transition: opacity 160ms ease; }
.pxp-z { position: absolute; left: 0; bottom: 0; font-family: 'Fredoka', 'Inter', sans-serif; font-weight: 700; color: #0065F8; opacity: 0; text-shadow: 0 1px 0 #FFFFFF, 0 0 6px rgba(255,255,255,0.9); animation: pxpZ 2.4s ease-out infinite; }
@keyframes pxpZ { 0% { opacity: 0; transform: translate(0, 0) scale(0.6) rotate(-10deg); } 20% { opacity: 1; } 100% { opacity: 0; transform: translate(-16px, -30px) scale(1.25) rotate(12deg); } }
.pxp-napcard:hover .pxp-nap-sleep { opacity: 0; }
.pxp-napcard:hover .pxp-nap-awake { opacity: 1; }
.pxp-napcard:hover .pxp-zzz { opacity: 0; }
.pxp-napcard:hover .pxp-nap { animation: pxpWake 380ms cubic-bezier(0.34,1.56,0.64,1) both; }
@keyframes pxpWake { 0% { transform: none; } 45% { transform: translateY(-5px) scale(0.98, 1.05); } 100% { transform: none; } }
video::cue { font-family: 'Fredoka', 'Inter', sans-serif; font-weight: 600; color: #FFFFFF; background: rgba(11,26,51,0.62); }
@keyframes pxpIn { from { opacity: 0; transform: translateY(8px) scale(0.97); } to { opacity: 1; transform: none; } }
.pxp-pop { animation: pxpIn 260ms cubic-bezier(0.22,1,0.36,1) both; }
@keyframes pxpRipple { from { opacity: 0.9; transform: scale(0.6); } to { opacity: 0; transform: scale(1.4); } }
.pxp-ripple { animation: pxpRipple 600ms ease-out both; }
@media (pointer: coarse) {
  .pxp-btn { min-width: 44px; min-height: 44px; }
  .pxp-play { transform: scale(1.12); }
}
@media (prefers-reduced-motion: reduce) { .pxp-pop, .pxp-ripple, .pxp-icon, .pxp-pip, .pxp-star-lit, .pxp-flag, .pxp-nap, .pxp-z { animation: none !important; }
@media (prefers-reduced-motion: reduce) { .pxp-z { opacity: 0.8; } } }
`;

export const PlayerStyles = () => <style>{css}</style>;

// Pip rides the progress bar: walks while the story plays, sits when it's
// paused, goes "whee" while you drag, and cheers with the flag at the end.
const PIP_POSES = {
  walk: '/assets/player/pip_walk.webp',
  sit: '/assets/player/pip_sit.webp',
  whee: '/assets/player/pip_whee.webp',
  cheer: '/assets/player/pip_cheer.webp',
};
// Width/height of each sprite, so every pose stands at the same height.
const PIP_ASPECT = { walk: 155 / 192, sit: 162 / 192, whee: 179 / 192, cheer: 215 / 192 };

const STAR_PATH = 'M12 2.5l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.3l-5.8 3.1 1.1-6.5L2.6 9.3l6.5-.9z';

/** A tiny star on the bar where each scene starts; it lights up once Pip passes. */
function SceneStar({ left, passed, size }) {
  return (
    <span
      aria-hidden="true"
      style={{ position: 'absolute', left: `${left}%`, top: '50%', width: size, height: size, transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 2 }}
    >
      <svg key={passed ? 'lit' : 'dim'} className={passed ? 'pxp-star-lit' : undefined} viewBox="0 0 24 24" width={size} height={size} style={{ display: 'block', overflow: 'visible' }}>
        <path d={STAR_PATH} fill={passed ? '#FFC93C' : '#FFFFFF'} stroke={passed ? '#E8A300' : 'rgba(11,26,51,0.28)'} strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

/** The finish line: a little yellow flag Pip grabs when the story ends. */
function FinishFlag({ size }) {
  return (
    <span aria-hidden="true" style={{ position: 'absolute', right: -2, bottom: '50%', width: size, height: size * 1.3, pointerEvents: 'none', zIndex: 2 }}>
      <svg viewBox="0 0 20 26" width={size} height={size * 1.3} style={{ display: 'block', overflow: 'visible' }}>
        <path d="M5 2v24" stroke="#A8744A" strokeWidth="2.2" strokeLinecap="round" />
        <path className="pxp-flag" d="M6 3c4 1.6 7.5-1.2 12 .6v8.4c-4.5-1.8-8 1-12-.6z" fill="#FFC93C" stroke="#E8A300" strokeWidth="1.1" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

const CONFETTI_COLORS = ['#0065F8', '#FFC93C', '#FF6B9A', '#3DD68C', '#FF8A3D', '#8B5CF6'];

/**
 * Paint-splat confetti bursting out of the finish flag when a story ends.
 * Drawn on its own fixed layer (inside the fullscreen element when there is
 * one) so the Up next card doesn't cover it.
 */
function PaintConfetti({ fire, anchorRef }) {
  const [spot, setSpot] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!fire) {
      setSpot(null);
      return;
    }
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const r = anchorRef.current?.getBoundingClientRect();
    if (r) setSpot({ x: r.right, y: r.top + r.height / 2, host: document.fullscreenElement || document.body });
  }, [fire, anchorRef]);

  useEffect(() => {
    if (!spot) return undefined;
    const canvas = ref.current;
    const ctx = canvas?.getContext?.('2d');
    if (!ctx) return undefined;
    const W = canvas.width;
    const H = canvas.height;
    const blobs = Array.from({ length: 56 }, () => {
      const a = -Math.PI / 2 + (Math.random() - 0.62) * 1.9;
      const v = 6 + Math.random() * 8;
      return {
        x: W - 40,
        y: H - 30,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v,
        r: 3 + Math.random() * 6,
        c: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        rot: Math.random() * Math.PI,
        squish: 0.55 + Math.random() * 0.4,
      };
    });
    let frame;
    let t = 0;
    const tick = () => {
      t += 1;
      ctx.clearRect(0, 0, W, H);
      const fade = Math.max(0, 1 - Math.max(0, t - 55) / 35);
      blobs.forEach((b) => {
        b.vy += 0.3;
        b.vx *= 0.985;
        b.x += b.vx;
        b.y += b.vy;
        b.rot += 0.05;
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(b.x, b.y);
        ctx.rotate(b.rot);
        ctx.fillStyle = b.c;
        ctx.beginPath();
        ctx.ellipse(0, 0, b.r, b.r * b.squish, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });
      if (fade > 0) frame = requestAnimationFrame(tick);
      else setSpot(null);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [spot]);

  if (!spot) return null;
  const W = 420;
  const H = 320;
  return createPortal(
    <canvas
      ref={ref}
      width={W}
      height={H}
      aria-hidden="true"
      style={{ position: 'fixed', left: spot.x - W + 40, top: spot.y - H + 30, width: W, height: H, pointerEvents: 'none', zIndex: 2147483000 }}
    />,
    spot.host
  );
}

/**
 * Progress bar split into rounded "chapters" (one per AI scene when we have
 * them), with Pip walking along it. Click or drag to seek; hover shows the
 * storyboard frame and scene title.
 */
export function ChapterProgress({ currentTime, duration, scenes, playback, onSeek, thin = false, playing = false }) {
  const barRef = useRef(null);
  const dragging = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const storyboard = useStoryboard(playback);
  const [hover, hoverHandlers] = useProgressHover(duration);

  const chapters = (() => {
    const starts = (scenes || []).map((s) => s.start).filter((s) => s > 0.5 && s < duration - 0.5);
    const edges = [0, ...starts, duration || 0];
    return edges.slice(0, -1).map((start, i) => ({ start, end: edges[i + 1] })).filter((c) => c.end > c.start);
  })();

  const seekAt = useCallback(
    (clientX) => {
      const el = barRef.current;
      if (!el || !duration) return;
      const r = el.getBoundingClientRect();
      onSeek(Math.max(0, Math.min(1, (clientX - r.left) / r.width)) * duration);
    },
    [duration, onSeek]
  );

  useEffect(() => {
    const move = (e) => dragging.current && seekAt(e.clientX);
    const up = () => {
      if (dragging.current) setIsDragging(false);
      dragging.current = false;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [seekAt]);

  const pct = duration ? Math.min(100, (currentTime / duration) * 100) : 0;
  const ended = duration > 0 && currentTime >= duration - 0.35;
  const pose = isDragging ? 'whee' : ended ? 'cheer' : playing ? 'walk' : 'sit';
  const h = thin ? 6 : 8;
  const pipH = thin ? 38 : 46;
  const starSize = thin ? 12 : 14;

  return (
    <div
      ref={barRef}
      className="pxp-bar"
      role="slider"
      tabIndex={0}
      aria-label="Seek"
      aria-valuemin={0}
      aria-valuemax={Math.round(duration || 0)}
      aria-valuenow={Math.round(currentTime || 0)}
      aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') onSeek(Math.min(duration, currentTime + 5));
        if (e.key === 'ArrowLeft') onSeek(Math.max(0, currentTime - 5));
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        dragging.current = true;
        setIsDragging(true);
        seekAt(e.clientX);
      }}
      onClick={(e) => e.stopPropagation()}
      {...hoverHandlers}
      style={{ position: 'relative', height: 22, display: 'flex', alignItems: 'center', cursor: 'pointer', touchAction: 'none' }}
    >
      <ScrubPreview hover={isDragging ? null : hover} board={storyboard} scenes={scenes} />
      <div style={{ display: 'flex', gap: 3, width: '100%', height: h }}>
        {chapters.map((c, i) => {
          const fill = duration ? Math.max(0, Math.min(1, (currentTime - c.start) / (c.end - c.start))) : 0;
          return (
            <div
              key={c.start}
              style={{
                flex: c.end - c.start,
                background: PX.sky,
                borderRadius: i === 0 && chapters.length === 1 ? 99 : i === 0 ? '99px 4px 4px 99px' : i === chapters.length - 1 ? '4px 99px 99px 4px' : 4,
                overflow: 'hidden',
              }}
            >
              <div className="pxp-paint" style={{ width: `${fill * 100}%`, height: '100%' }} />
            </div>
          );
        })}
      </div>
      {duration > 0 &&
        chapters.slice(1).map((c) => (
          <SceneStar key={`star-${c.start}`} left={(c.start / duration) * 100} passed={currentTime >= c.start} size={starSize} />
        ))}
      {duration > 0 && !ended && <FinishFlag size={thin ? 13 : 15} />}
      <PaintConfetti fire={ended} anchorRef={barRef} />
      {/* Pip, standing on the playhead */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: `clamp(${pipH * 0.4}px, ${pct}%, calc(100% - ${pipH * 0.4}px))`,
          bottom: `calc(50% - ${h / 2 + 2}px)`,
          width: pipH * PIP_ASPECT.cheer,
          height: pipH,
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
          zIndex: 3,
          transition: isDragging ? 'none' : 'left 250ms linear',
        }}
      >
        {Object.keys(PIP_POSES).map((k) => (
          <img
            key={k}
            src={PIP_POSES[k]}
            alt=""
            draggable={false}
            className={k === pose ? `pxp-pip pxp-pip-${k}` : 'pxp-pip'}
            style={{
              position: 'absolute',
              left: '50%',
              bottom: 0,
              height: pipH,
              width: pipH * PIP_ASPECT[k],
              marginLeft: -(pipH * PIP_ASPECT[k]) / 2,
              opacity: k === pose ? 1 : 0,
            }}
          />
        ))}
      </span>
    </div>
  );
}

/**
 * Center card while paused mid-story: a big play button and which scene, with
 * Pip napping on top of the card (breathing, z's floating up). Hover the card
 * and he wakes up.
 */
export function PausedCard({ scene, title, onPlay, compact = false }) {
  const napH = compact ? 50 : 62;
  const napW = napH * (313 / 160);
  return (
    <div
      className="pxp-pop pxp-napcard"
      onClick={(e) => e.stopPropagation()}
      style={{
        ...panelStyle,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 10 : 14,
        borderRadius: compact ? 18 : 22,
        padding: compact ? '10px 12px 10px 10px' : '14px 16px 14px 14px',
        width: 'max-content',
        minWidth: compact ? 240 : 300,
        maxWidth: 'min(92vw, 480px)',
      }}
    >
      {/* Pip napping on the card: belly on the top edge, a foot dangling over */}
      <span
        aria-hidden="true"
        style={{ position: 'absolute', right: compact ? 12 : 18, bottom: `calc(100% - ${Math.round(napH * 0.17)}px)`, width: napW, height: napH, pointerEvents: 'none', zIndex: 2 }}
      >
        <span className="pxp-zzz" style={{ position: 'absolute', left: napW * 0.1, top: -napH * 0.12, width: 40, height: 40 }}>
          {[0, 1, 2].map((i) => (
            <span key={i} className="pxp-z" style={{ animationDelay: `${i * 0.8}s`, fontSize: compact ? 16 : 20 }}>
              z
            </span>
          ))}
        </span>
        <span className="pxp-nap" style={{ position: 'absolute', inset: 0 }}>
          <img className="pxp-nap-sleep" src="/assets/player/pip_nap.webp" alt="" draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
          <img className="pxp-nap-awake" src="/assets/player/pip_nap_awake.webp" alt="" draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
        </span>
      </span>
      <button
        type="button"
        className="pxp-play"
        aria-label="Keep watching"
        onClick={(e) => {
          e.stopPropagation();
          onPlay();
        }}
        style={{
          width: compact ? 44 : 52,
          height: compact ? 44 : 52,
          borderRadius: '50%',
          border: 'none',
          background: PX.blue,
          color: '#fff',
          boxShadow: `0 3px 0 ${PX.blueDeep}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <svg width={compact ? 20 : 24} height={compact ? 20 : 24} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ marginLeft: 2 }}>
          <path d="M8 5v14l11-7z" />
        </svg>
      </button>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ color: PX.accentText, fontSize: compact ? 12 : 13, fontWeight: 600 }}>
          {scene ? `Paused on scene ${scene.index + 1}` : 'Paused'}
        </div>
        <div
          style={{
            fontSize: compact ? 15 : 18,
            fontWeight: 700,
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {scene?.title || title}
        </div>
      </div>
    </div>
  );
}

/** Buffering: Pip painting instead of a spinner. */
export function PipLoader({ size = 120, dark = true }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: dark ? 'rgba(11,26,51,0.35)' : 'transparent',
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      <PandaLoader variant="inline" size={size} withBackdrop={false} />
    </div>
  );
}

/** "Continue from 1:12 · Start over" for something half-watched. */
export function ResumeChip({ seconds, onResume, onStartOver }) {
  return (
    <div
      className="pxp-pop"
      onClick={(e) => e.stopPropagation()}
      style={{ ...panelStyle, display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 999, padding: 5 }}
    >
      <button
        type="button"
        className="pxp-play"
        onClick={(e) => {
          e.stopPropagation();
          onResume();
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: PX.blue,
          color: '#fff',
          border: 'none',
          borderRadius: 999,
          padding: '9px 16px',
          fontFamily: PX.font,
          fontSize: 15,
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow: `0 3px 0 ${PX.blueDeep}`,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M8 5v14l11-7z" />
        </svg>
        Continue from {formatTime(seconds)}
      </button>
      <button
        type="button"
        className="pxp-btn"
        onClick={(e) => {
          e.stopPropagation();
          onStartOver();
        }}
        style={{ background: 'transparent', border: 'none', color: PX.inkSoft, borderRadius: 999, padding: '9px 12px', fontFamily: PX.font, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
      >
        Start over
      </button>
    </div>
  );
}

/**
 * Double-tap the left or right side to skip 10s (touch screens). Returns
 * { onTouchEnd, ripple } — spread onTouchEnd on the video area and render
 * <SeekRipple ripple={ripple} />.
 */
export function useDoubleTapSeek(videoRef, { enabled = true } = {}) {
  const last = useRef({ t: 0, side: null });
  const [ripple, setRipple] = useState(null);
  const onTouchEnd = useCallback(
    (e) => {
      if (!enabled) return;
      const touch = e.changedTouches?.[0];
      const video = videoRef.current;
      if (!touch || !video) return;
      const r = e.currentTarget.getBoundingClientRect();
      const side = touch.clientX - r.left < r.width / 2 ? 'left' : 'right';
      const now = Date.now();
      if (now - last.current.t < 300 && last.current.side === side) {
        e.preventDefault();
        const delta = side === 'left' ? -10 : 10;
        video.currentTime = Math.max(0, Math.min(video.duration || Infinity, video.currentTime + delta));
        setRipple({ side, key: now });
        last.current = { t: 0, side: null };
      } else {
        last.current = { t: now, side };
      }
    },
    [enabled, videoRef]
  );
  return { onTouchEnd, ripple };
}

export function SeekRipple({ ripple }) {
  if (!ripple) return null;
  return (
    <div
      key={ripple.key}
      className="pxp-ripple"
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: '50%',
        [ripple.side]: '12%',
        transform: 'translateY(-50%)',
        width: 88,
        height: 88,
        marginTop: -44,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.7)',
        backdropFilter: 'blur(14px)',
        color: PX.ink,
        fontFamily: PX.font,
        fontWeight: 700,
        fontSize: 18,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 25,
      }}
    >
      {ripple.side === 'left' ? '−10s' : '+10s'}
    </div>
  );
}

/** Side drawer of a show's episodes, to jump without leaving the player. */
export function EpisodesDrawer({ open, episodes, currentId, isLocked, onSelect, onLocked, onClose, showTitle }) {
  if (!open) return null;
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      style={{ position: 'absolute', inset: 0, zIndex: 40, background: 'rgba(11,26,51,0.22)' }}
    >
      <aside
        className="pxp-pop"
        aria-label="Episodes"
        onClick={(e) => e.stopPropagation()}
        style={{
          ...panelStyle,
          background: 'rgba(255,255,255,0.8)',
          position: 'absolute',
          top: 12,
          right: 12,
          bottom: 12,
          width: 'min(360px, calc(100% - 24px))',
          borderRadius: 22,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 10px' }}>
          <div>
            <div style={{ color: PX.accentText, fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Episodes</div>
            {showTitle && <div style={{ fontSize: 18, fontWeight: 700 }}>{showTitle}</div>}
          </div>
          <button type="button" className="pxp-btn" aria-label="Close episodes" onClick={onClose} style={{ border: 'none', background: 'transparent', borderRadius: 999, width: 36, height: 36, cursor: 'pointer', color: PX.ink }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: '0 10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {episodes.map((ep, i) => {
            const locked = isLocked?.(ep, i);
            const current = ep.id === currentId;
            return (
              <button
                key={ep.id}
                type="button"
                onClick={() => (locked ? onLocked?.() : onSelect(ep, i))}
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  textAlign: 'left',
                  padding: 6,
                  borderRadius: 14,
                  border: current ? `2px solid ${PX.blueBright}` : '2px solid transparent',
                  background: current ? PX.pale : 'transparent',
                  cursor: 'pointer',
                  fontFamily: PX.font,
                  color: PX.ink,
                }}
              >
                <span style={{ position: 'relative', width: 104, aspectRatio: '16 / 9', borderRadius: 10, overflow: 'hidden', background: PX.sky, flexShrink: 0 }}>
                  {ep.thumbnail_url && <img src={ep.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: locked ? 'brightness(0.6)' : 'none' }} />}
                  {locked && (
                    <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                        <rect x="5" y="11" width="14" height="10" rx="2" />
                        <path d="M8 11V8a4 4 0 018 0v3" />
                      </svg>
                    </span>
                  )}
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 12, color: current ? PX.accentText : PX.inkSoft, fontWeight: 600 }}>
                    {current ? 'Now playing' : isSpecialEpisode(ep) ? getSpecialLabel(ep) : locked ? `Episode ${i + 1} · Members` : `Episode ${i + 1}`}
                  </span>
                  <span style={{ display: 'block', fontSize: 15, fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190 }}>
                    {ep.name}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </aside>
    </div>
  );
}
